import { z } from 'zod'
import { ApiError, type ApiClient } from '@fusapp-work/api'
import { hasUniformHeadcountCutoff, unitSchema, unitSchemas, unitsSchema, writeVersionSchema, type Unit } from './company'
import { parseResponse } from '../../shared/response'
import { collectListPages, consolePageSize, listPageSchema } from '../../shared/listPage'

const treeRow = z.object({
  kind: z.enum(['Department', 'Team']), id: z.string().min(1), companyId: z.string().min(1), version: writeVersionSchema, name: z.string(), code: z.string().nullable(),
  status: z.enum(['Active', 'Inactive']), parentId: z.string().nullable(), sortOrder: z.number().int(), depth: z.number().int().nonnegative(),
  childCount: z.number().int().nonnegative(), orphan: z.boolean(), managerName: z.null(), headcount: z.number().int().nonnegative().nullable(),
  headcountState: z.enum(['known', 'unknown', 'unavailable', 'unsupported']), headcountAsOf: z.string().nullable(), archivedAt: z.null(), archiveReason: z.null(),
}).refine(row => row.kind === 'Team' ? row.headcountState === 'unsupported' : row.headcountState !== 'unsupported', 'Invalid unit headcount kind')
const structurePage = listPageSchema(treeRow, 'tree:asc').refine(page => hasUniformHeadcountCutoff(page.items), 'Inconsistent headcount cutoff')
const branchPage = listPageSchema(unitSchemas.branches, 'name:asc').refine(page => hasUniformHeadcountCutoff(page.items), 'Inconsistent headcount cutoff')
const legacyUnits = unitsSchema.and(z.object({ items: z.never().optional(), page: z.never().optional(), size: z.never().optional(), totalCount: z.never().optional(), totalUnfiltered: z.never().optional(), appliedSort: z.never().optional() }))
const structureResponse = z.union([structurePage, legacyUnits])

function scoped<T extends { companyId: string }>(rows: T[], companyId: string): T[] {
  if (rows.some(row => row.companyId !== companyId)) throw new ApiError('Invalid company response scope', 200, 'INVALID_RESPONSE')
  return rows
}

/** Adapt the paged wire tree to the existing chart/editor model, after every page is available. */
export async function readCompanyUnits(client: ApiClient, path: string, companyId: string, signal?: AbortSignal) {
  const url = (kind: 'Structure' | 'Branch', page: number) => `${path}?kind=${kind}&page=${page}&size=${consolePageSize}`
  const first = parseResponse(structureResponse, await client.request<unknown>(url('Structure', 1), { signal }))
  if ('departments' in first) {
    scoped([...first.departments, ...first.teams, ...first.branches], companyId)
    return first
  }
  const readStructure = async (page: number) => parseResponse(structurePage, await client.request<unknown>(url('Structure', page), { signal }))
  const readBranches = async (page: number) => parseResponse(branchPage, await client.request<unknown>(url('Branch', page), { signal }))
  const [treeRows, branchRows] = await Promise.all([
    collectListPages(first, readStructure),
    readBranches(1).then(first => collectListPages(first, readBranches)),
  ])
  const tree = scoped(treeRows, companyId), branches = scoped(branchRows, companyId)
  const departments: Unit[] = [], teams: Unit[] = []
  for (const row of tree) {
    const unit = parseResponse(unitSchema, { ...row, parentDepartmentId: row.kind === 'Department' ? row.parentId : null, departmentId: row.kind === 'Team' ? row.parentId : null })
    if (row.kind === 'Department') departments.push(unit)
    else teams.push(unit)
  }
  return { departments, teams, branches }
}
